import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';

/** 筛选器项的可能值类型：数组或嵌套对象（如 region: { markets: [...] }） */
type TFilterValue =
  | unknown[]
  | { markets?: unknown[]; [key: string]: unknown; }
  | undefined;

/**
 * 从筛选器值中提取实际选中值（兼容数组和嵌套对象两种结构）
 * - 数组: system: ["255"]  →  ["255"]
 * - 嵌套: campaign: { campaign: ["255"], ... }  →  ["255"]
 *         ua_network: { ua_network: ["255"], ... }  →  ["255"]
 *         region: { markets: ["255"], ... }  →  ["255"]
 */
function extractFilterList(key: string, value: TFilterValue): unknown[] | undefined {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') {
    if (key === 'region') return (value as { markets?: unknown[]; }).markets;
    return (value as Record<string, unknown[] | undefined>)[key];
  }
  return undefined;
}

function isDefault(list: unknown[] | undefined): boolean {
  if (!list || list.length === 0) return true;
  return list.length === 1 && (list[0] === 255 || list[0] === '255');
}

const DIM_KEYS = ['system', 'campaign', 'ua_network', 'region'] as const;

const DEFAULT_HIDE_DELAY = 300;

function shouldShowAiAnalysis(filterVal: Record<string, unknown> = {}): boolean {
  const { rangeTime } = filterVal as { rangeTime?: unknown[]; };

  const isSingleDay =
    Array.isArray(rangeTime) &&
    rangeTime.length >= 2 &&
    rangeTime[0] === rangeTime[1];

  if (!isSingleDay) return false;

  return DIM_KEYS.every((key) =>
    isDefault(extractFilterList(key, filterVal[key] as TFilterValue)),
  );
}

/** Guard 组件 props：children 为需要守卫的子组件，data/changeData/aiEnabled 来自 pageConfig 编排 */
interface IAiAnalysisGuardProps {
  children?: React.ReactNode;
  data?: unknown;
  changeData?: (key: string, value: unknown) => void;
  hideDelay?: number;
  /** 明确关闭时优先级最高，直接隐藏 AI 卡片 */
  aiEnabled?: boolean;
  /** 允许透传其它 props 到子组件 */
  [key: string]: unknown;
}

/**
 * 条件守卫：仅在「单天 + 维度筛选器为默认值 255」时渲染子组件，
 * 否则延迟淡出后卸载，避免内容闪缩。
 */
export default function AiAnalysisGuard(props: IAiAnalysisGuardProps) {
  const { children, data, changeData, hideDelay, aiEnabled, ...restProps } = props;
  const delay = hideDelay ?? DEFAULT_HIDE_DELAY;
  const { filterVal } = useContext(FilterContext) || {};

  // aiEnabled 为 false 时，直接隐藏 AI 卡片（优先级最高）
  const aiDisabled = aiEnabled === false;

  const shouldShow = useMemo(
    () => !aiDisabled && shouldShowAiAnalysis(filterVal),
    [filterVal, aiDisabled],
  );

  const [mounted, setMounted] = useState(shouldShow);
  const [visible, setVisible] = useState(shouldShow);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (shouldShow) {
      setMounted(true);
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
      timerRef.current = setTimeout(() => setMounted(false), delay);
    }
    return () => clearTimeout(timerRef.current);
  }, [shouldShow, delay]);

  if (!mounted) return null;

  return (
    <>
      {React.Children.map(children, (child) => {
        // children 运行时可能包含文本/空节点，仅对有效 React 元素做 cloneElement
        if (!React.isValidElement(child)) return child;
        return React.cloneElement(child, {
          data,
          changeData,
          ...restProps,
          style: {
            ...(child.props?.style || {}),
            opacity: visible ? 1 : 0,
            transition: `opacity ${delay}ms ease`,
          },
        } satisfies React.Attributes & Record<string, unknown>);
      })}
    </>
  );
}
